import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../utils/api';
import { addLeafletCompassControl } from '../utils/leafletCompassControl';
import { DASHBOARD_DEFAULT_ZOOM, INTA_CORDOBA_CENTER, INTA_CORDOBA_LABEL } from '../utils/mapDefaults';
import DashboardTerritorialHeatmap from '../components/DashboardTerritorialHeatmap';
import {
  FiBarChart2,
  FiClipboard,
  FiDatabase,
  FiInfo,
  FiLayers,
  FiList,
  FiMapPin,
  FiNavigation,
  FiTrendingUp,
} from 'react-icons/fi';

const DEFAULT_CENTER = INTA_CORDOBA_CENTER;
const DEFAULT_ZOOM = DASHBOARD_DEFAULT_ZOOM;
const FOCUS_ZOOM = 12;
const MONTH_FORMATTER = new Intl.DateTimeFormat('es-AR', { month: 'short', year: '2-digit' });
const WEEK_LABEL_FORMATTER = new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit' });
const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const parseCoordinate = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value) => {
  const parsed = parseDate(value);
  if (!parsed) {
    return 'Sin fecha';
  }

  return DATE_LABEL_FORMATTER.format(parsed);
};

const getCaseTimelineDateValue = (caso = {}) => caso.fecha_caso || caso.fecha_ingreso || '';
const getCaseDiagnosisLabel = (caso = {}) => caso.diagnostico_primario || caso.diagnostico_secundario || 'Sin diagnostico';

const normalizeSpeciesLabel = (value) => {
  if (!value) {
    return 'Sin especie';
  }

  return value
    .toLowerCase()
    .split('_')
    .map((chunk) => (chunk ? `${chunk[0].toUpperCase()}${chunk.slice(1)}` : chunk))
    .join(' ');
};

const getStartOfWeek = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  const day = normalized.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  normalized.setDate(normalized.getDate() + diff);
  return normalized;
};

const buildMonthSeries = (casos, monthsBack = 6) => {
  const today = new Date();
  const firstMonth = new Date(today.getFullYear(), today.getMonth() - (monthsBack - 1), 1);
  const monthMap = new Map();

  for (let index = 0; index < monthsBack; index += 1) {
    const current = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + index, 1);
    const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
    monthMap.set(key, {
      key,
      label: MONTH_FORMATTER.format(current).replace('.', ''),
      value: 0,
    });
  }

  casos.forEach((caso) => {
    const fecha = parseDate(getCaseTimelineDateValue(caso));
    if (!fecha) {
      return;
    }

    const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
    if (monthMap.has(key)) {
      monthMap.get(key).value += 1;
    }
  });

  return Array.from(monthMap.values());
};

const buildWeekSeries = (casos, weeksBack = 8) => {
  const currentWeek = getStartOfWeek(new Date());
  const firstWeek = new Date(currentWeek);
  firstWeek.setDate(currentWeek.getDate() - (weeksBack - 1) * 7);
  const weekMap = new Map();

  for (let index = 0; index < weeksBack; index += 1) {
    const current = new Date(firstWeek);
    current.setDate(firstWeek.getDate() + index * 7);
    const key = current.toISOString().slice(0, 10);
    weekMap.set(key, {
      key,
      label: WEEK_LABEL_FORMATTER.format(current),
      value: 0,
    });
  }

  casos.forEach((caso) => {
    const fecha = parseDate(getCaseTimelineDateValue(caso));
    if (!fecha) {
      return;
    }

    const weekStart = getStartOfWeek(fecha).toISOString().slice(0, 10);
    if (weekMap.has(weekStart)) {
      weekMap.get(weekStart).value += 1;
    }
  });

  return Array.from(weekMap.values());
};

const buildDominantSpeciesByMonth = (casos, monthsBack = 6) => {
  const today = new Date();
  const firstMonth = new Date(today.getFullYear(), today.getMonth() - (monthsBack - 1), 1);
  const monthMap = new Map();

  for (let index = 0; index < monthsBack; index += 1) {
    const current = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + index, 1);
    const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
    monthMap.set(key, {
      key,
      label: MONTH_FORMATTER.format(current).replace('.', ''),
      total: 0,
      dominantLabel: 'Sin especie',
      dominantCount: 0,
      share: 0,
      speciesMap: new Map(),
    });
  }

  casos.forEach((caso) => {
    const fecha = parseDate(getCaseTimelineDateValue(caso));
    if (!fecha) {
      return;
    }

    const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
    const monthEntry = monthMap.get(key);
    if (!monthEntry) {
      return;
    }

    const muestras = caso.muestras || [];
    if (muestras.length === 0) {
      monthEntry.total += 1;
      monthEntry.speciesMap.set('Sin especie', (monthEntry.speciesMap.get('Sin especie') || 0) + 1);
      return;
    }

    muestras.forEach((muestra) => {
      const label = normalizeSpeciesLabel(muestra.grupo_zootecnico);
      monthEntry.total += 1;
      monthEntry.speciesMap.set(label, (monthEntry.speciesMap.get(label) || 0) + 1);
    });
  });

  return Array.from(monthMap.values()).map((item) => {
    let dominantLabel = 'Sin registros';
    let dominantCount = 0;

    item.speciesMap.forEach((value, label) => {
      if (value > dominantCount || (value === dominantCount && label.localeCompare(dominantLabel) < 0)) {
        dominantLabel = label;
        dominantCount = value;
      }
    });

    return {
      key: item.key,
      label: item.label,
      total: item.total,
      dominantLabel,
      dominantCount,
      share: item.total > 0 ? dominantCount / item.total : 0,
    };
  });
};

const InfoTooltip = ({ description }) => (
  <span className="info-tooltip">
    <span className="info-badge" tabIndex={0} aria-label={description}>
      <FiInfo aria-hidden="true" />
    </span>
    <span className="info-tooltip__content" role="tooltip">
      {description}
    </span>
  </span>
);

const InfoHeading = ({ title, description }) => (
  <div className="info-block">
    <div className="info-block__title-row">
      <h3 className="form-title">{title}</h3>
      <InfoTooltip description={description} />
    </div>
  </div>
);

const StatCard = ({ label, value, description, hint, icon: Icon, accent = 'blue' }) => (
  <article className="dashboard-stat-card">
    <div className="dashboard-stat-card__top">
      <div className="dashboard-stat-card__title-group">
        <span className={`dashboard-stat-card__icon dashboard-stat-card__icon--${accent}`}>
          <Icon aria-hidden="true" />
        </span>
        <span className="dashboard-stat-card__label">{label}</span>
      </div>
      <InfoTooltip description={description} />
    </div>
    <strong className="dashboard-stat-card__value">{value}</strong>
    {hint ? <span className="dashboard-stat-card__hint">{hint}</span> : null}
  </article>
);

const TimeSeriesChart = ({ title, subtitle, description, data, tone = 'blue' }) => {
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  return (
    <article className="surface-card analytics-panel">
      <div className="analytics-panel__header">
        <InfoHeading title={title} description={description} />
        <span className="protocol-chip">{data.reduce((total, item) => total + item.value, 0)} casos</span>
      </div>
        <p className="page-subtitle analytics-panel__subtitle">{subtitle}</p>

        <div className="time-chart" role="img" aria-label={title}>
          {data.map((item) => (
            <div key={item.key} className="time-chart__item">
              <span className="time-chart__value">{item.value}</span>
              <div className="time-chart__bar-wrap" title={`${item.label}: ${item.value}`}>
                <div
                  className={`time-chart__bar time-chart__bar--${tone}`}
                  style={{ height: `${Math.max((item.value / maxValue) * 100, item.value > 0 ? 8 : 2)}%` }}
                />
              </div>
              <span className="time-chart__label">{item.label}</span>
            </div>
          ))}
        </div>
    </article>
  );
};

const HorizontalBarList = ({ title, subtitle, description, data, valueLabel }) => {
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  return (
    <article className="surface-card analytics-panel">
      <div className="analytics-panel__header">
        <InfoHeading title={title} description={description} />
      </div>
      <p className="page-subtitle analytics-panel__subtitle">{subtitle}</p>

      <div className="ranking-list">
        {data.length === 0 ? (
          <div className="dashboard-empty dashboard-empty--compact">Todavía no hay datos suficientes.</div>
        ) : (
          data.map((item) => (
            <div key={item.label} className="ranking-list__item">
              <div className="ranking-list__row">
                <strong>{item.label}</strong>
                <span>{valueLabel(item.value)}</span>
              </div>
              <div className="ranking-list__track">
                <div className="ranking-list__fill" style={{ width: `${(item.value / maxValue) * 100}%` }} />
              </div>
            </div>
          ))
        )}
      </div>
    </article>
  );
};

const MonthlySpeciesChart = ({ title, subtitle, description, data }) => (
  <article className="surface-card analytics-panel analytics-panel--species">
    <div className="analytics-panel__header">
      <InfoHeading title={title} description={description} />
      <span className="protocol-chip">{data.filter((item) => item.total > 0).length} meses activos</span>
    </div>
    <p className="page-subtitle analytics-panel__subtitle">{subtitle}</p>

    <div className="species-month-grid">
      {data.map((item) => (
        <article key={item.key} className="species-month-card">
          <div className="species-month-card__top">
            <span className="species-month-card__month">{item.label}</span>
            <span className="species-month-card__count">
              {item.total > 0 ? `${item.dominantCount}/${item.total}` : 'Sin datos'}
            </span>
          </div>
          <strong className="species-month-card__species">{item.dominantLabel}</strong>
          <div className="species-month-card__track" aria-hidden="true">
            <div
              className="species-month-card__fill"
              style={{ width: `${Math.max(item.share * 100, item.total > 0 ? 10 : 0)}%` }}
            />
          </div>
          <p className="species-month-card__meta">
            {item.total > 0
              ? `${Math.round(item.share * 100)}% de los registros de ese mes`
              : 'Todavía no hay registros en este mes'}
          </p>
        </article>
      ))}
    </div>
  </article>
);

const CasosDashboardPage = () => {
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);
  const markerMapRef = useRef(new Map());

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/ingresos_casos/dashboard/'))
      .then((response) => {
        if (!active) {
          return;
        }

        setCasos(response.data);
        setSelectedId('');
        setLoading(false);
      })
      .catch((fetchError) => {
        if (!active) {
          return;
        }

        console.error('Error al obtener casos:', fetchError);
        setError('No se pudieron cargar los casos para el dashboard.');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const casosFiltrados = useMemo(() => {
    const desdeDate = parseDate(desde);
    const hastaDate = parseDate(hasta);

    return casos.filter((caso) => {
      const fechaCaso = parseDate(getCaseTimelineDateValue(caso));
      if (!fechaCaso) {
        return !desdeDate && !hastaDate;
      }

      if (desdeDate && fechaCaso < desdeDate) {
        return false;
      }

      if (hastaDate && fechaCaso > hastaDate) {
        return false;
      }

      return true;
    });
  }, [casos, desde, hasta]);

  const casosConUbicacion = useMemo(
    () =>
      casosFiltrados
        .map((caso) => ({
          ...caso,
          latitudNumerica: parseCoordinate(caso.latitud),
          longitudNumerica: parseCoordinate(caso.longitud),
        }))
        .filter((caso) => caso.latitudNumerica !== null && caso.longitudNumerica !== null),
    [casosFiltrados]
  );

  useEffect(() => {
    if (!selectedId || casosConUbicacion.some((caso) => caso.protocolo_general === selectedId)) {
      return;
    }

    setSelectedId('');
  }, [casosConUbicacion, selectedId]);

  const selectedCase = casosConUbicacion.find((caso) => caso.protocolo_general === selectedId) || null;

  const analytics = useMemo(() => {
    const sampleRecords = casosFiltrados.flatMap((caso) => caso.muestras || []);
    const totalPhysicalSamples = sampleRecords.reduce(
      (total, muestra) => total + (Number(muestra.numero_de_muestras) || 0),
      0
    );
    const casesPerSpeciesMap = new Map();

    casosFiltrados.forEach((caso) => {
      const speciesInCase = new Set(
        (caso.muestras || [])
          .map((muestra) => normalizeSpeciesLabel(muestra.grupo_zootecnico))
          .filter(Boolean)
      );

      if (speciesInCase.size === 0) {
        speciesInCase.add('Sin especie');
      }

      speciesInCase.forEach((species) => {
        casesPerSpeciesMap.set(species, (casesPerSpeciesMap.get(species) || 0) + 1);
      });
    });

    const samplesPerCase = casosFiltrados
      .map((caso) => {
        const muestras = caso.muestras || [];
        const total = muestras.reduce((sum, muestra) => sum + (Number(muestra.numero_de_muestras) || 0), 0);
        return {
          label: caso.protocolo_general,
          value: total,
          sampleRecords: muestras.length,
        };
      })
      .sort((a, b) => b.value - a.value || b.sampleRecords - a.sampleRecords || a.label.localeCompare(b.label))
      .slice(0, 6);

    const casesPerSpecies = Array.from(casesPerSpeciesMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
      .slice(0, 6);

    const averagePhysicalSamples = casosFiltrados.length > 0 ? (totalPhysicalSamples / casosFiltrados.length).toFixed(1) : '0';
    const averageSampleRecords = casosFiltrados.length > 0 ? (sampleRecords.length / casosFiltrados.length).toFixed(1) : '0';
    const dominantSpeciesOverall = casesPerSpecies[0] || null;

    return {
      totalSampleRecords: sampleRecords.length,
      totalPhysicalSamples,
      averagePhysicalSamples,
      averageSampleRecords,
      casesPerSpecies,
      dominantSpeciesOverall,
      samplesPerCase,
      dominantSpeciesByMonth: buildDominantSpeciesByMonth(casosFiltrados),
      monthSeries: buildMonthSeries(casosFiltrados),
      weekSeries: buildWeekSeries(casosFiltrados),
    };
  }, [casosFiltrados]);

  useEffect(() => {
    if (loading || error || casosConUbicacion.length === 0) {
      return undefined;
    }

    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    if (!window.L) {
      setError('No se pudo cargar la librería del mapa.');
      return undefined;
    }

    const map = window.L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
    });

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    addLeafletCompassControl(window.L, map, {
      title: 'Brujula: el mapa mantiene el norte hacia arriba.',
    });

    layerGroupRef.current = window.L.layerGroup().addTo(map);
    mapRef.current = map;

    window.setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => {
      map.remove();
      mapRef.current = null;
      layerGroupRef.current = null;
      markerMapRef.current = new Map();
    };
  }, [loading, error, casosConUbicacion.length]);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerGroupRef.current;
    if (!map || !layerGroup || !window.L) {
      return;
    }

    layerGroup.clearLayers();
    markerMapRef.current = new Map();

    if (casosConUbicacion.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    casosConUbicacion.forEach((caso) => {
      const marker = window.L.marker([caso.latitudNumerica, caso.longitudNumerica]);
      marker.bindPopup(`
        <strong>Caso ${caso.protocolo_general}</strong><br/>
        Diagnostico: ${getCaseDiagnosisLabel(caso)}<br/>
        Propietario: ${caso.propietario || 'Sin propietario'}<br/>
        Fecha: ${formatDateLabel(getCaseTimelineDateValue(caso))}<br/>
        <a href="/ver-caso/${caso.protocolo_general}" class="btn btn-brand btn-sm" style="margin-top:8px; display:inline-block; color:#fff; text-decoration:none;">
          Ver caso
        </a>
      `);
      marker.on('click', () => {
        setSelectedId(caso.protocolo_general);
      });
      marker.addTo(layerGroup);
      markerMapRef.current.set(caso.protocolo_general, marker);
    });

    if (selectedCase) {
      map.setView([selectedCase.latitudNumerica, selectedCase.longitudNumerica], FOCUS_ZOOM);
      const selectedMarker = markerMapRef.current.get(selectedCase.protocolo_general);
      selectedMarker?.openPopup();
      return;
    }

    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  }, [casosConUbicacion, selectedCase]);

  const handleFocusCase = (caso) => {
    setSelectedId(caso.protocolo_general);
    const map = mapRef.current;
    const marker = markerMapRef.current.get(caso.protocolo_general);

    if (map) {
      map.setView([caso.latitudNumerica, caso.longitudNumerica], FOCUS_ZOOM);
    }

    marker?.openPopup();
  };

  const rangoActivo = useMemo(() => {
    if (!desde && !hasta) {
      const fechasDisponibles = casosFiltrados
        .map((caso) => parseDate(getCaseTimelineDateValue(caso)))
        .filter(Boolean)
        .sort((a, b) => a - b);

      if (fechasDisponibles.length === 0) {
        return 'Sin fechas disponibles';
      }

      const primeraFecha = formatDateInputValue(fechasDisponibles[0]);
      const ultimaFecha = formatDateInputValue(fechasDisponibles[fechasDisponibles.length - 1]);

      return `${formatDateLabel(primeraFecha)} a ${formatDateLabel(ultimaFecha)}`;
    }

    if (desde && hasta) {
      return `${formatDateLabel(desde)} a ${formatDateLabel(hasta)}`;
    }

    if (desde) {
      return `Desde ${formatDateLabel(desde)}`;
    }

    return `Hasta ${formatDateLabel(hasta)}`;
  }, [casosFiltrados, desde, hasta]);

  const limpiarFiltros = () => {
    setDesde('');
    setHasta('');
  };

  const casosSinUbicacion = casosFiltrados.length - casosConUbicacion.length;

  return (
    <section className="container-fluid page-shell dashboard-page" aria-busy={loading}>
      {loading ? (
        <div className="dashboard-page-loading-overlay" role="status" aria-live="polite">
          <div className="dashboard-page-loading-overlay__card">
            <span className="inline-loader__spinner" aria-hidden="true" />
            <strong>Cargando dashboard...</strong>
            <span>Estamos preparando el mapa, las métricas y los casos visibles.</span>
          </div>
        </div>
      ) : null}
      <div className="page-header">
        <div className="dashboard-page-header__content">
          <span className="section-kicker">Dashboard</span>
          <h2 className="page-title">Mapa general y analítica de casos</h2>
          <p className="page-subtitle">
            Visualiza los casos georreferenciados y sigue la evolución temporal usando la fecha del caso cuando está disponible.
          </p>
          <div className="dashboard-header-highlights">
            <div className="dashboard-header-pill">
              <FiTrendingUp aria-hidden="true" />
              <span>{analytics.monthSeries.reduce((total, item) => total + item.value, 0)} ingresos en los ultimos 6 meses</span>
            </div>
            <div className="dashboard-header-pill">
              <FiList aria-hidden="true" />
              <span>
                Especie líder: {analytics.dominantSpeciesOverall ? analytics.dominantSpeciesOverall.label : 'Sin datos'}
              </span>
            </div>
          </div>
        </div>
        <img src="/logo-inta.png" alt="Logo INTA" className="page-header-logo" />
      </div>

      <div className="surface-card case-list-card">
        <div className="case-list-toolbar dashboard-filter-toolbar">
          <div className="dashboard-filter-toolbar__controls">
            <div className="dashboard-filter-toolbar__heading">
              <span className="section-kicker">Rango de fechas</span>
              <p className="page-subtitle dashboard-filter-toolbar__copy">
                Ajusta el periodo para revisar los casos según la fecha del caso y, si no existe, la fecha de ingreso.
              </p>
            </div>

            <div className="case-list-filters case-list-filters--dashboard">
              <label className="case-list-filter">
                <span>Desde</span>
                <input
                  type="date"
                  className="form-control"
                  value={desde}
                  max={hasta || undefined}
                  onChange={(event) => setDesde(event.target.value)}
                />
              </label>
              <label className="case-list-filter">
                <span>Hasta</span>
                <input
                  type="date"
                  className="form-control"
                  value={hasta}
                  min={desde || undefined}
                  onChange={(event) => setHasta(event.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="case-list-toolbar__summary dashboard-filter-toolbar__summary">
            <div className="case-time-bubble">
              <span className="case-time-bubble__label">Rango visible</span>
              <strong>{rangoActivo}</strong>
            </div>

            <div className="dashboard-filter-toolbar__actions">
              <div className="protocol-chip protocol-chip--dashboard">{casosFiltrados.length} casos visibles</div>
              <button
                type="button"
                className="btn btn-outline-brand dashboard-filter-toolbar__button"
                onClick={limpiarFiltros}
                disabled={!desde && !hasta}
              >
                Limpiar filtro
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-stats dashboard-stats--expanded row g-3">
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Casos totales"
            value={casosFiltrados.length}
            description="Cantidad total de casos clí nicos visibles dentro del rango temporal activo."
            icon={FiClipboard}
            accent="blue"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Con ubicacion"
            value={casosConUbicacion.length}
            description="Casos visibles que tienen latitud y longitud registradas para mostrarse en el mapa."
            icon={FiMapPin}
            accent="sky"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Sin punto"
            value={casosSinUbicacion}
            description="Casos visibles que todavía no tienen coordenadas cargadas o válidas."
            icon={FiNavigation}
            accent="slate"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Registros de muestras"
            value={analytics.totalSampleRecords}
            description="Cantidad de formularios de muestra asociados a los casos visibles."
            icon={FiLayers}
            accent="blue"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Muestras declaradas"
            value={analytics.totalPhysicalSamples}
            description="Suma del campo número de muestras declarado en cada registro visible."
            icon={FiDatabase}
            accent="sky"
          />
        </div>
        <div className="col-12 col-sm-6 col-xl-4">
          <StatCard
            label="Promedio por caso"
            value={analytics.averagePhysicalSamples}
            description="Promedio de muestras declaradas por cada caso visible."
            hint={`${analytics.averageSampleRecords} registros/caso`}
            icon={FiBarChart2}
            accent="blue"
          />
        </div>
      </div>

      <div className="dashboard-layout row g-3">
        <div className="col-12 col-xxl-8">
          <div className="surface-card dashboard-map-panel">
            <div className="dashboard-map-panel__header">
              <InfoHeading
                title="Casos georreferenciados"
                description="Mapa interactivo de los casos visibles que tienen coordenadas registradas."
              />
              <Link to="/ingreso-caso" className="btn btn-outline-brand">
                Cargar nuevo caso
              </Link>
            </div>
            <p className="page-subtitle analytics-panel__subtitle">
              El mapa inicia centrado en {INTA_CORDOBA_LABEL}. Toca un marcador o selecciona un caso de la lista para enfocarlo.
            </p>

            {loading ? (
              <div className="dashboard-empty">Cargando dashboard...</div>
            ) : error ? (
              <div className="alert subtle-alert mb-0">{error}</div>
            ) : casosConUbicacion.length === 0 ? (
              <div className="dashboard-empty">
                No hay casos con coordenadas dentro del rango temporal seleccionado.
              </div>
            ) : (
              <div ref={mapContainerRef} className="dashboard-map" />
            )}
          </div>
        </div>

        <div className="col-12 col-xxl-4">
          <aside className="surface-card dashboard-side-panel">
            <div className="dashboard-side-panel__top">
              <InfoHeading
                title="Casos ubicados"
                description="Listado de casos visibles en el mapa para navegar rápido entre ubicaciones."
              />
              <span className="protocol-chip">{casosConUbicacion.length} visibles</span>
            </div>

              <div className="dashboard-side-panel__body">
                <div className="dashboard-case-list">
                {loading ? (
                  <div className="dashboard-empty dashboard-empty--compact">Cargando casos...</div>
                ) : casosConUbicacion.length === 0 ? (
                  <div className="dashboard-empty dashboard-empty--compact">No hay casos con punto cargado.</div>
                ) : (
                  casosConUbicacion.map((caso) => {
                    const isActive = caso.protocolo_general === selectedCase?.protocolo_general;

                    return (
                      <button
                        key={caso.protocolo_general}
                        type="button"
                        className={`dashboard-case-item${isActive ? ' dashboard-case-item--active' : ''}`}
                        onClick={() => handleFocusCase(caso)}
                      >
                        <strong>{caso.protocolo_general}</strong>
                        <span>{caso.propietario || 'Sin propietario'}</span>
                        <span>
                          {caso.provincia || 'Sin provincia'}
                          {caso.localidad ? ` / ${caso.localidad}` : ''}
                        </span>
                        <span>Fecha: {formatDateLabel(getCaseTimelineDateValue(caso))}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <DashboardTerritorialHeatmap desde={desde} hasta={hasta} casos={casosFiltrados} />

      <section className="analytics-section">
        <div className="analytics-section__header">
          <div>
            <span className="section-kicker">Metricas</span>
            <h3 className="page-title analytics-section__title">Tendencias y distribución clínica</h3>
            <p className="page-subtitle">
              Resumen temporal y operativo construido a partir de la fecha del caso o, en su defecto, la fecha de ingreso.
            </p>
          </div>
        </div>

        <div className="analytics-grid analytics-grid--time row g-3">
          <div className="col-12 col-xl-6">
            <TimeSeriesChart
              title="Casos por semana"
              subtitle="Últimas 8 semanas dentro del rango visible."
              description="Muestra como evolucionó la carga de casos semana a semana usando la fecha del caso cuando existe."
              data={analytics.weekSeries}
              tone="blue"
            />
          </div>
          <div className="col-12 col-xl-6">
            <TimeSeriesChart
              title="Casos por mes"
              subtitle="Últimos 6 meses dentro del rango visible."
              description="Resume el volumen mensual de casos según la fecha del caso o la fecha de ingreso como respaldo."
              data={analytics.monthSeries}
              tone="teal"
            />
          </div>
          <div className="col-12">
            <MonthlySpeciesChart
              title="Especie predominante por mes"
              subtitle="Lectura mensual de la especie con mayor presencia dentro de los registros visibles."
              description="Agrupa los registros de muestra por mes usando la fecha del caso y destaca la especie mas frecuente en cada periodo."
              data={analytics.dominantSpeciesByMonth}
            />
          </div>
        </div>

        <div className="analytics-grid analytics-grid--distribution row g-3">
          <div className="col-12 col-xl-6">
            <HorizontalBarList
              title="Casos por especie"
              subtitle="Cuenta única de casos por grupo zootécnico registrado."
              description="Cuenta cada caso una sola vez por especie, aunque tenga varias muestras."
              data={analytics.casesPerSpecies}
              valueLabel={(value) => `${value} caso${value === 1 ? '' : 's'}`}
            />
          </div>
          <div className="col-12 col-xl-6">
            <HorizontalBarList
              title="Muestras por caso"
              subtitle="Ranking según la suma declarada en número de muestras."
              description="Compara los casos con mayor volumen de muestras declaradas."
              data={analytics.samplesPerCase}
              valueLabel={(value) => `${value} muestras`}
            />
          </div>
        </div>
      </section>
    </section>
  );
};

export default CasosDashboardPage;
